/**
 * Keyword-based product recommendations (product detail page).
 *
 * The recommendations section (sections/product-recommendations.liquid) matches the current
 * product's title against client-managed `recommendation_rule` metaobjects server-side. When a
 * rule matches it emits data-keyword-* attributes and sets data-recommendations-performed="true"
 * so the native related-products loader (assets/product-recommendations.js) stays off and this
 * script owns the section instead.
 *
 * Why this approach: this store offers no reliable way to render arbitrary title-search results
 * server-side (the Section Rendering API returns null/404 for the recommendations section outside
 * the product template, and the native /recommendations endpoint only does Shopify's own related/
 * complementary intents). The storefront predictive-search JSON (/search/suggest.json) is the one
 * dependable title lookup, so we fetch candidates there, filter them to the rule's exact AND/OR
 * keyword semantics, and render the cards client-side. Sold-out grayscale comes from the JSON
 * `available` flag; the pre-order badge reuses the same Globo data as preorder-badge-gate.js.
 *
 * If a rule matches but yields no products, we fall back to Shopify's native recommendations so
 * the section is never left empty.
 */
(function () {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /**
   * Split a comma-separated keyword attribute into a clean, lowercased, de-duplicated list.
   * @param {string | undefined} value
   * @returns {string[]}
   */
  function parseKeywords(value) {
    if (!value) return [];
    const seen = new Set();
    value
      .toLowerCase()
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((kw) => seen.add(kw));
    return Array.from(seen);
  }

  /**
   * Build the set of product ids that belong to an ACTIVE (Ongoing) Globo pre-order offer — the
   * same signal preorder-badge-gate.js uses. Returns null when the Globo data isn't ready, in
   * which case we simply render no pre-order badge (fail-soft).
   * @returns {Record<string, true> | null}
   */
  function activePreorderSet() {
    const params = window.GloboPreorderParams;
    const settings = params && params.preorderSettings;
    if (!settings || !settings.products || !settings.profiles) return null;
    if (!Object.keys(settings.products).length || !Object.keys(settings.profiles).length) return null;

    const profiles = settings.profiles;
    const productMap = settings.products;
    const active = Object.create(null);
    Object.keys(productMap).forEach((productId) => {
      const variantToProfile = productMap[productId] || {};
      const isActive = Object.keys(variantToProfile).some((variantId) => {
        const profile = profiles[variantToProfile[variantId]];
        return profile && Number(profile.status) === 1;
      });
      if (isActive) active[String(productId)] = true;
    });
    return active;
  }

  /**
   * Fetch predictive-search product matches for a single keyword.
   * @param {string} keyword
   * @returns {Promise<Array<object>>}
   */
  async function fetchSuggestions(keyword) {
    const url =
      `${ROOT}search/suggest.json?q=${encodeURIComponent(keyword)}` +
      `&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      return (data.resources && data.resources.results && data.resources.results.products) || [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Format a money amount using the store currency. Predictive search returns major units.
   * @param {number|string} amount
   * @returns {string}
   */
  function formatMoney(amount) {
    const value = parseFloat(amount);
    if (Number.isNaN(value)) return '';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch (e) {
      return `${currency} ${value.toFixed(2)}`;
    }
  }

  /**
   * Build one recommendation card matching the look of the native cards (portrait media, rounded,
   * sold-out grayscale, pre-order / sold-out badge).
   * @param {object} product
   * @param {Record<string, true> | null} preorders
   * @returns {string}
   */
  function cardHtml(product, preorders) {
    // Predictive search returns `image` as a string URL but `featured_image` as an object
    // ({ url, ... }); normalise to a string either way.
    let image = product.image || product.featured_image || '';
    if (image && typeof image === 'object') image = image.url || image.src || '';
    const imageSrc =
      typeof image === 'string' && image
        ? `${image}${image.indexOf('?') === -1 ? '?' : '&'}width=500`
        : '';
    const available = product.available !== false;
    const isPreorder = available && preorders && preorders[String(product.id)];

    let badge = '';
    if (isPreorder) {
      badge = '<div class="kw-reco-card__badge kw-reco-card__badge--preorder">Pre Order</div>';
    } else if (!available) {
      badge = '<div class="kw-reco-card__badge kw-reco-card__badge--soldout">Sold out</div>';
    }

    const mediaInner = imageSrc
      ? `<img src="${imageSrc}" alt="${escapeHtml(product.title)}" loading="lazy">`
      : '';

    return (
      '<div class="resource-list__item">' +
      `<product-card class="product-card kw-reco-card${available ? '' : ' kw-reco-card--sold-out'}" data-product-id="${product.id}">` +
      `<a class="kw-reco-card__link" href="${ROOT.replace(/\/$/, '')}${product.url}">` +
      `<span class="kw-reco-card__media">${mediaInner}${badge}</span>` +
      `<span class="kw-reco-card__title">${escapeHtml(product.title)}</span>` +
      `<span class="kw-reco-card__price">${formatMoney(product.price)}</span>` +
      '</a></product-card></div>'
    );
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Replace the section's results list (skeleton) with the given inner HTML.
   * @param {HTMLElement} el
   * @param {string} itemsHtml
   */
  function renderCards(el, itemsHtml) {
    const skeleton = el.querySelector('.resource-list');
    if (!skeleton) return;
    const rail = document.createElement('div');
    rail.className = 'product-recommendations__rail';
    rail.innerHTML =
      '<div class="resource-list resource-list--grid" data-testid="resource-list-grid" data-has-recommendations="true">' +
      itemsHtml +
      '</div>';
    skeleton.replaceWith(rail);
    el.classList.remove('hidden');
    delete el.dataset.error;
  }

  /**
   * Fall back to Shopify's native related products (same fetch the native loader would do).
   * @param {HTMLElement} el
   */
  async function nativeFallback(el) {
    const { url, productId, sectionId, intent } = el.dataset;
    if (!url || !productId) {
      el.classList.add('hidden');
      return;
    }
    try {
      const response = await fetch(`${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`);
      if (!response.ok) throw new Error(`status ${response.status}`);
      const text = await response.text();
      const holder = document.createElement('div');
      holder.innerHTML = text;
      const fresh = holder.querySelector(`product-recommendations[id="${el.id}"]`);
      if (fresh && fresh.innerHTML.trim().length) {
        el.innerHTML = fresh.innerHTML;
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    } catch (e) {
      el.classList.add('hidden');
    }
  }

  /**
   * Load keyword recommendations for a single section element.
   * @param {HTMLElement} el
   */
  async function load(el) {
    const all = parseKeywords(el.dataset.keywordAll);
    const any = parseKeywords(el.dataset.keywordAny);
    const maxProducts = parseInt(el.dataset.maxProducts || '4', 10);
    const selfId = String(el.dataset.productId || '');
    const lookupKeywords = Array.from(new Set([...all, ...any])).slice(0, 6);

    if (lookupKeywords.length === 0) {
      nativeFallback(el);
      return;
    }

    // Gather candidates for every keyword, then keep only products whose title satisfies the rule:
    // ALL of `all` present AND at least one of `any` present (for whichever list is non-empty).
    const batches = await Promise.all(lookupKeywords.map(fetchSuggestions));
    const byId = new Map();
    batches.flat().forEach((product) => {
      if (product && !byId.has(product.id)) byId.set(product.id, product);
    });

    const matches = Array.from(byId.values()).filter((product) => {
      if (String(product.id) === selfId) return false;
      const title = String(product.title || '').toLowerCase();
      if (all.length && !all.every((kw) => title.includes(kw))) return false;
      if (any.length && !any.some((kw) => title.includes(kw))) return false;
      return true;
    });

    // Prefer available products first, then trim to the configured count.
    matches.sort((a, b) => (a.available === b.available ? 0 : a.available ? -1 : 1));
    const selected = matches.slice(0, maxProducts);

    if (selected.length === 0) {
      nativeFallback(el);
      return;
    }

    const preorders = activePreorderSet();
    renderCards(el, selected.map((product) => cardHtml(product, preorders)).join(''));
  }

  function init() {
    const sections = document.querySelectorAll('product-recommendations[data-keyword-active="true"]');
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          load(/** @type {HTMLElement} */ (entry.target));
        });
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    sections.forEach((section) => observer.observe(section));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
